<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Transaction ID Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.transaction_id')"
                v-model.trim="filterOptions.transaction_id"
              />
              <!-- End:: Transaction ID Input -->

              <!-- Start:: Store Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.store_name')"
                v-model.trim="filterOptions.brand_name"
              />
              <!-- End:: Store Name Input -->

              <!-- Start:: Announcement Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.announcement_id')"
                v-model.trim="filterOptions.number"
              />
              <!-- End:: Announcement Number Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="3"
                :optionsList="announcementStatuses"
                :placeholder="$t('PLACEHOLDERS.announcement_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->

              <!-- Start:: City Input -->
              <base-select-input
                col="3"
                :optionsList="cities"
                :placeholder="$t('PLACEHOLDERS.city')"
                v-model="filterOptions.city_id"
              />
              <!-- End:: City Input -->

              <!-- Start:: Date From Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_from')"
                v-model.trim="filterOptions.date_from"
              />
              <!-- End:: Date From Input -->

              <!-- Start:: Date To Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_to')"
                v-model.trim="filterOptions.date_to"
              />
              <!-- End:: Date To Input -->
            </div>

            <div class="btns_wrapper">
              <button
                class="submit_btn"
                :disabled="isWaitingRequest"
                type="submit"
              >
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.store_announcement_payments") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <span>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </span>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Package Price -->
        <template v-slot:[`item.package_price`]="{ item }">
          <span>{{ item.package_price }} {{ $t("PLACEHOLDERS.sar") }}</span>
        </template>
        <!-- End:: Package Price -->

        <template v-slot:[`item.provider_number`]="{ item }">
          <span>{{ item?.store?.mobile }}</span>
        </template>
        <!-- Start:: Payment Date -->
        <template v-slot:[`item.payed_at`]="{ item }">
          <span>{{ formatDateTime(item.payed_at) }}</span>
        </template>
        <!-- End:: Payment Date -->

        <!-- Start:: Publication Status -->
        <template v-slot:[`item.is_published`]="{ item }">
          <v-chip
            :color="item.is_published ? 'success' : 'error'"
            text-color="white"
            small
          >
            {{
              item.is_published
                ? $t("PLACEHOLDERS.published")
                : $t("PLACEHOLDERS.expired")
            }}
          </v-chip>
        </template>
        <!-- End:: Publication Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- View Details -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button
                v-if="$can('providerpayments show', 'providerpayments')"
                class="btn_show"
                @click="showDetails(item)"
              >
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllStorePayments",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    announcementStatuses() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.published"),
          value: "published",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.expired"),
          value: "expired",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.pending"),
          value: "pending",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        transaction_id: null,
        brand_name: null,
        number: null,
        status: null,
        city_id: null,
        date_from: null,
        date_to: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("PLACEHOLDERS.serial_number"),
          value: "serial",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.transaction_id"),
          value: "transaction_id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.announcement_id"),
          value: "id",
          align: "center",
          width: "100",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.announcement_name"),
          value: "name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.store_name"),
          value: "store_brand_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.provider_number"),
          value: "provider_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.target_users_count"),
          value: "users_count",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.package_days"),
          value: "duration_in_days",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.package_name"),
          value: "package_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.amount_paid"),
          value: "package_price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.payment_date_time"),
          value: "payed_at",
          align: "center",
          width: "160",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.announcement_status"),
          value: "is_published",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "100",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Cities Data
      cities: [],
      // End:: Cities Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    // Start:: Format Date Time
    formatDateTime(dateString) {
      if (!dateString) return "-";
      const date = dateString.split(" ");
      return date.length > 1 ? `${date[0]} ${date[1]}` : date[0];
    },
    // End:: Format Date Time

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async setTableRows() {
      this.loading = true;
      try {
        let params = {
          page: this.paginations.current_page,
        };

        // Add filter parameters if they exist
        if (this.filterOptions.transaction_id) {
          params.transaction_id = this.filterOptions.transaction_id;
        }
        if (this.filterOptions.brand_name) {
          params.brand_name = this.filterOptions.brand_name;
        }
        if (this.filterOptions.number) {
          params.number = this.filterOptions.number;
        }
        if (this.filterOptions.status) {
          params.status =
            this.filterOptions.status.value || this.filterOptions.status;
        }
        if (this.filterOptions.city_id) {
          params.city_id =
            this.filterOptions.city_id.id || this.filterOptions.city_id;
        }
        if (this.filterOptions.date_from) {
          params.date_from = this.filterOptions.date_from;
        }
        if (this.filterOptions.date_to) {
          params.date_to = this.filterOptions.date_to;
        }

        let res = await this.$axios({
          method: "GET",
          url: "announcements/store-payments",
          params: params,
        });
        this.loading = false;

        this.tableRows = res.data.data.data.map((item) => ({
          id: item.id,
          name: item.name,
          store_id: item.store_id,
          store_brand_name: item.store_brand_name,
          package_price: item.package_price,
          transaction_id: item.transaction_id || "-",
          payed_at: item.payed_at,
          is_published: item.is_published,
          package_name: item.package?.name || "-",
          users_count: item.package?.users_count || 0,
          duration_in_days: item.package?.duration_in_days || 0,
          store: item.store || {},
        }));

        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Details
    showDetails(item) {
      this.$router.push({ path: `/store-payments/show/${item.id}` });
    },
    // End:: Show Details

    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/store-payments/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.transaction_id = null;
      this.filterOptions.brand_name = null;
      this.filterOptions.number = null;
      this.filterOptions.status = null;
      this.filterOptions.city_id = null;
      this.filterOptions.date_from = null;
      this.filterOptions.date_to = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/store-payments/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Get Cities
    async getCities() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "cities",
        });
        this.cities = res.data.data.map((city) => ({
          id: city.id,
          name: this.getAppLocale === "ar" ? city.name_ar : city.name_en,
        }));
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Cities
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.getCities();
    this.setTableRows();
  },
};
</script>
